export type CanonStatus =
  | "canon"
  | "draft"
  | "verify"
  | "legacy"
  | "contradiction"
  | "rejected";

export type SourceRef = {
  id?: string;
  label: string;
  path?: string;
  chapter?: number | null;
  note?: string;
};

export type EntityMeta = {
  id: string;
  status: CanonStatus;
  sources: SourceRef[];
  createdAt?: string;
  updatedAt?: string;
  decisionNote?: string;
  tags?: string[];
};

export type Book = EntityMeta & {
  number: number;
  title: string;
  writingStatus: "planned" | "writing" | "complete";
};

export type Branch = EntityMeta & {
  name: string;
  kind: "main" | "flashback" | "alternate" | "loop" | "intersection" | "hypothesis";
  description: string;
  parentBranchId?: string | null;
  divergenceMomentId?: string | null;
};

export type Moment = EntityMeta & {
  bookId: string;
  chapterNumber: number;
  sceneNumber?: number | null;
  title?: string;
  label: string;
  readingOrder: number;
  storyOrder?: number | null;
  branchId: string;
  loopIterationId?: string | null;
  parentMomentId?: string | null;
};

export type LifeStatus =
  | "alive"
  | "injured"
  | "unconscious"
  | "missing"
  | "presumed-dead"
  | "dead";

export const STATE_PATCH_FIELDS = [
  "age",
  "className",
  "rank",
  "guild",
  "location",
  "physicalState",
  "psychologicalState",
  "lifeStatus",
  "immediateGoal",
  "notes",
] as const;

export type StatePatch = {
  id: string;
  validFromMomentId: string;
  validToMomentId?: string | null;
  branchId: string;
  loopIterationId?: string | null;
  age?: number | string | null;
  className?: string | null;
  rank?: number | null;
  guild?: string | null;
  location?: string | null;
  physicalState?: string | null;
  psychologicalState?: string | null;
  lifeStatus?: LifeStatus | null;
  immediateGoal?: string | null;
  notes?: string;
  status: CanonStatus;
  sources: SourceRef[];
};

export type Character = EntityMeta & {
  name: string;
  aliases?: string[];
  role: string;
  ageAtAwakening?: string;
  origin?: string;
  family?: string[];
  appearance?: string;
  className: string;
  gift: string;
  summary: string;
  traits?: string[];
  abilities?: string[];
  states: StatePatch[];
};

export type StoryEvent = EntityMeta & {
  momentId: string;
  title: string;
  summary: string;
  importance?: "major" | "supporting" | "minor";
  characterIds: string[];
  locations?: string[];
  consequences?: string[];
  branchId: string;
};

export type Relationship = EntityMeta & {
  fromCharacterId: string;
  toCharacterId: string;
  type: string;
  label: string;
  summary?: string;
  reciprocal?: boolean;
  validFromMomentId: string;
  validToMomentId?: string | null;
  branchId: string;
};

export type Knowledge = {
  characterId: string;
  knownFromMomentId: string;
  knownToMomentId?: string | null;
  branchId?: string;
  certainty: "suspects" | "believes" | "knows" | "misunderstands";
  believedVersion?: string;
};

export type Secret = EntityMeta & {
  title: string;
  objectiveTruth: string;
  trueFromMomentId: string;
  revealedToReaderAtMomentId?: string | null;
  branchId: string;
  knownBy: Knowledge[];
};

export type ContinuityIssue = EntityMeta & {
  severity: "critical" | "important" | "attention" | "info";
  state:
    | "open"
    | "investigating"
    | "decision-needed"
    | "resolved"
    | "accepted"
    | "false-positive";
  title: string;
  description: string;
  entityRefs: string[];
  momentIds?: string[];
  resolution?: string;
};

export type BibleMeta = {
  title: string;
  updatedAt: string;
  description?: string;
};

export type Bible = {
  schemaVersion: number;
  meta: BibleMeta;
  books: Book[];
  branches: Branch[];
  moments: Moment[];
  characters: Character[];
  events: StoryEvent[];
  relationships: Relationship[];
  secrets: Secret[];
  continuityIssues: ContinuityIssue[];
};

export type ResolvedCharacterState = {
  characterId: string;
  momentId: string | null;
  branchId: string | null;
  loopIterationId: string | null;
  isGeneralView: boolean;
  age?: number | string | null;
  className?: string | null;
  rank?: number | null;
  guild?: string | null;
  location?: string | null;
  physicalState?: string | null;
  psychologicalState?: string | null;
  lifeStatus?: LifeStatus | null;
  immediateGoal?: string | null;
  notes?: string;
  appliedPatchIds: string[];
};

export type TimeSelection = {
  bookId: string | null;
  momentId: string | null;
  orderMode: "reading" | "story";
  viewer: "author" | "reader";
};

export type ValidationError = {
  path: string;
  message: string;
};

export type ChangeOperation = {
  operation: "create" | "update" | "delete";
  entityType: string;
  entityId: string;
  before?: unknown;
  after?: unknown;
};

export type BibleEnvelope = {
  bible: Bible;
  revisionNumber: number;
  updatedAt: string;
  source: "remote" | "seed" | "local";
};
